<template>
  <div>
    <page-snippet class="mt-2 mb-2" v-if="true" name="new-contributor" />

    <b-form-group
      :class="{ required: true, 'mt-4': true }"
      id="nameType-group"
      label="Name type"
      label-for="nameType-select">

      <b-form-select
        id="nameType-select"
        v-model="contributor.NameType"
        :options="nameTypeOptions"
      />
    </b-form-group>

    <div v-if="contributor.NameType == 'person'">
      <b-form-group
        :class="{ 'mt-4': true }"
        id="name1-group"
        label="First name"
        label-for="part1-input">

        <b-form-input
          id="part1-input"
          ref="part1-input"
          v-model="contributor.NamePart1"
          type="text"
        />
      </b-form-group>

      <b-form-group
        :class="{ 'mt-4': true }"
        id="name3-group"
        label="Last name"
        label-for="part3-input">

        <b-form-input
          id="part3-input"
          ref="part3-input"
          v-model="contributor.NamePart3"
          type="text"
        />
      </b-form-group>
    </div>

    <div v-if="contributor.NameType == 'entity'">
      <b-form-group
        :class="{ 'mt-4': true }"
        id="name3-group"
        label="Name"
        label-for="part3-input">

        <b-form-input
          id="part3-input"
          ref="part3-input"
          v-model="contributor.NamePart3"
          type="text"
        />
      </b-form-group>
    </div>

    <b-form-checkbox
      :id="'contributor-name-auto-formatting'"
      v-model="contributor.AutoFormatting"
    >
      {{ $t('Automatically apply standard casing / styling') }}
    </b-form-checkbox>

    <field 
      :defs="defs"
      name="Contributors.Country"
      :model="contributor" />

    <b-form-group
      :class="{ required: true, 'mt-4': true }"
      id="existingProfiles-group"
      :label="$t('Does this contributor have existing profiles on Music Services')"
      label-for="existingProfiles-select">

      <b-form-select
        id="existingProfiles-select"
        v-model="contributor.HasExistingProfiles"
        :options="exitingProfilesOptions"
      />
    </b-form-group>

    <b-form-group
      v-if="['unsure', 'yes'].includes(contributor.HasExistingProfiles)"
      :class="{ required: true, 'mt-4': true }"
      id="profileInformation-group"
      :label="$t('Profile information')"
      label-for="profileInformation-input"
      :description="$t('Please enter as much information as you have on existing profiles. URL\'s to profile pages, contributor home page etc.')"
    >

      <b-form-textarea
        id="profileInformation-input"
        v-model="contributor.ProfileInformation"
        placeholder=""
        rows="3"
        max-rows="6"
      />
    </b-form-group>

    <b-form-group
      :class="{ required: true, 'mt-4': true }"
      id="roles-group"
      :label="$t('Contributor Roles')"
      :description="roleDef.Description"
      label-for="roless-select"
    >
      <div class="mb-1">
        {{ $t('Selected Roles') }}:
        <em v-if="contributor.Roles.length == 0">No roles selected, please select at least one from the list below.</em>
        <ul class="inline-comma-list font-weight-bold" v-if="contributor.Roles.length > 0">
          <li v-for="(role, index) in contributor.Roles" :key="index">
            {{ role }}
            <a class="text-danger" href="#" @click.prevent="deleteRole(index)">
              <b-icon icon="x" shift-v="3" font-scale="1.5" />
            </a>
          </li>
        </ul>
      </div>

      <b-input-group>
        <b-form-select
          id="roles-select"
          v-model="selectedRole"
          :options="roles"
        />
        <b-input-group-append>
          <b-button variant="primary" @click="addSelectedRole">{{ $t('Add') }}</b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-alert show v-for="(warning, index) in warnings" :key="index" variant="warning" v-html="warning"> 
    </b-alert>
      
    <b-alert :show="showErrors" v-for="(error, index) in errors" :key="index" variant="danger">
      {{ error }}
    </b-alert>

    <b-button :disabled="creatingContributor" @click="$emit('cancel')" variant="secondary">{{ $t('Cancel') }}</b-button>

    <b-button :disabled="creatingContributor" @click="create()" variant="primary" class="ml-3">
      <template v-if="fullName == ''">
        {{ $t('Create') }}
      </template>

      <template v-if="fullName != ''">
        {{ $t('Create') }} '{{ fullName }}'
      </template>

      <b-spinner small v-if="creatingContributor"></b-spinner>
    </b-button>
  </div>
</template>

<script>
  import { formatName } from '@/functions'

  export default {
    name: 'ContributorCreateForm',
    components: {
      'page-snippet': () => import('@/components/PageSnippet'),
      'field': () => import('@/components/ReleaseConceptField'),
    },
    data () {
      return {
        showErrors: false,
        creatingContributor: false,
        selectedRole: '',
        contributor: {
          HasExistingProfiles: '',
          NameType: 'person',
          AutoFormatting: true,
          NamePart1: '',
          NamePart3: '',
          ProfileInformation: '',
          Roles: [],
        },
        nameTypeOptions: [
          { value: 'person', text: this.$t('Person') },
          { value: 'entity', text: this.$t('Entity (Company, Band)') },
        ],
        exitingProfilesOptions: [
          { value: '', text: '' },
          { value: 'yes', text: this.$t('Yes, but I was unable to find them using search') },
          { value: 'unsure', text: this.$t('Maybe, I\'m not sure') },
          { value: 'no', text: this.$t('No, this Contributor needs a fresh profile') },
        ],
      }
    },
    props: {
      defs: Object,
      'role-picker': Boolean,
    },
    methods: {
      addSelectedRole () {
        if (!this.selectedRole) return
        if (this.contributor.Roles.includes(this.selectedRole)) return
        this.contributor.Roles.push(this.selectedRole)
      },
      deleteRole (index) {
        this.contributor.Roles.splice(index, 1);
      },
      create () {
        if (this.creatingContributor) return

        if (this.contributor.AutoFormatting) {
          this.contributor.NamePart1 = formatName(this.contributor.NamePart1)
          this.contributor.NamePart3 = formatName(this.contributor.NamePart3)
        }

        if (this.errors.length > 0) {
          this.showErrors = true
          return
        }

        let roles = [...this.contributor.Roles]
        let c = JSON.parse(JSON.stringify(this.contributor))

        if (c.NameType == 'entity') {
          delete c.Name1
          delete c.Name2
        }

        delete c.NameType
        delete c.Roles
        delete c.AutoFormatting

        this.creatingContributor = true
        
        this.$http.post('contributors', c)
          .then((response) => {
            c.Identifiers = response.data.Identifiers
            this.$emit('create', c, roles)
            this.creatingContributor = false
          })
          .catch((error) => {
            console.log('Error', error)
            this.$notify({
              type: 'warn',
              text: this.$t('Something went wrong while creating Contributor, please try again.'),
            })
            this.creatingContributor = false
          })
      },
    },
    computed: {
      roleDef () {
        return this.defs['Contributors.Role']
      },
      roles () {
        return this.roleDef.Options.map(cr => { 
          return {
            value: cr.JsonValue,
            text: cr.Value,
          }
        }).filter(cr => {
          return !this.contributor.Roles.includes(cr.value)
        })
      },
      fullName () {
        let name = []
        if (this.contributor.NamePart1) {
          if (this.contributor.AutoFormatting)
            name.push(formatName(this.contributor.NamePart1))
          else
            name.push(this.contributor.NamePart1)
        }
        
        if (this.contributor.NamePart3) {
          if (this.contributor.AutoFormatting)
            name.push(formatName(this.contributor.NamePart3))
          else
            name.push(this.contributor.NamePart3)
        }

        return name.join(' ').trim()
      },
      errors () {
        let errors = []
        if (!this.contributor.NamePart3.trim()) {
          if (this.contributor.NameType == 'person')
            errors.push(this.$t('Last name is mandatory'))
          else
            errors.push(this.$t('Name is mandatory'))

        }

        if (this.fullName.indexOf(',') != -1) {
          errors.push(this.$t('Comma\'s are not allowed in contributor names'))
        }

        if (!this.contributor.HasExistingProfiles) {
          errors.push(this.$t('Please select if the contributor has existing profiles'))
        }

        if (['unsure', 'yes'].includes(this.contributor.HasExistingProfiles) && 
            this.contributor.ProfileInformation.length < 10) {
          errors.push(this.$t('Please provide information on existing profiles'))
        }

        if (this.contributor.Roles.length == 0) {
          errors.push(this.$t('Role is mandatory'))
        }

        return errors
      },
      warnings () {
        let warnings = []
        let name = this.fullName.toLowerCase()
        if (name.split(' ').length > 4 || [' with ', ' and ', ' en ', '&', '/'].some(i => name.includes(i))) {
          warnings.push(this.$root.$data.pageSnippets['validate-compound-artist'])
        }

        let numCaps = name.replace(/[^A-Z]/g, '').length
        let numNormal = name.replace(/[^a-z]/g, '').length
        if ((numCaps+numNormal > 3) && (numCaps >= numNormal)) {
          warnings.push('Name is over 50% capitalized, that is usually an error.')
        }
        return warnings
      },
    },
    mounted () {

    }
  }
</script>

<style lang="scss" scoped>

</style>